import _ from 'underscore';
import React, {
	useEffect,
	useRef,
	useMemo,
	useState,
	useCallback
} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import escapeStringRegexp from 'escape-string-regexp';
import { api } from '@culture/common-helpers/request';

import dynamic from 'next/dynamic';

import { DEFAULT_LIMIT } from 'utils/helpers/constants';
import FilterButton from '../Button';

import styles from './styles.module.scss';

const Popup = dynamic(() => import('components/Popup'), {
	ssr: false
});
const List = dynamic(() => import('./components/List'), {
	ssr: false
});

const CustomSelect = ({
	name,
	searchable = false,
	placeholder,
	resetOption = { title: 'Показать все', name: '' },
	selectedValue = '',
	selectedOption,
	controlTitleField = 'title',
	accent = false,
	label = '',
	onChange,
	isLoading = false,
	options,
	valueField = 'name',
	maxTitleLength,
	style,
	hasClearButton = false,
	apiParams,
	buttonTheme,
	titleClassName
}) => {
	const [selected, setSelected] = useState(null);
	const [isPopupVisible, setIsPopupVisible] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const [previousSearchValue, setPreviousSearchValue] = useState('');
	const [isUpdating, setIsUpdating] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [pagination, setPagination] = useState(null);

	const controlRef = useRef(null);

	const [optionsList, setOptionsList] = useState(options || []);

	const handleSearch = (event) => {
		setPreviousSearchValue(searchValue);
		setSearchValue(event.target.value);
	};

	const fetchData = useMemo(() => {
		if (_.isEmpty(apiParams)) {
			return null;
		}

		return ({ query, cached = false, extraParams, isLoadMore = false }) => {
			const {
				url,
				affectQueryParams,
				paginationType,
				withoutCache
			} = apiParams;
			const cachedResult = !withoutCache ? { cached } : undefined;

			if (isLoadMore) {
				setIsLoadingMore(true);
			} else {
				setIsUpdating(true);
			}
			return api(url, {
				...affectQueryParams,
				...extraParams,
				...cachedResult,
				query
			})
				.then((data) => {
					const dataItems =
						paginationType === 'pagination'
							? data.items
							: data[apiParams?.dataPath];

					const items = apiParams?.additionalTitlePath
						? dataItems.map((item) => {
								const title = item[apiParams.additionalTitlePath]
									? `${item.title} (${item[apiParams.additionalTitlePath]})`
									: item.title;
								return {
									...item,
									title
								};
						  })
						: dataItems;

					if (dataItems?.length === 0 && isLoadMore) {
						return;
					}

					if (paginationType === 'pagination') {
						setPagination(data.pagination);
					}

					setOptionsList((prevState) =>
						_.uniq(
							isLoadMore ? [...prevState, ...items] : items,
							(item) => item._id
						)
					);
				})
				.finally(() => {
					_.delay(() => {
						setIsUpdating(false);
						setIsLoadingMore(false);
					}, 300);
				});
		};
	}, [apiParams?.affectQueryParams]);

	const fetchDataDebounced = useMemo(
		() => fetchData && _.debounce(fetchData, apiParams.debounce),
		[fetchData]
	);

	const filterOptions = (searchQuery) => {
		const searchRegexp = new RegExp(escapeStringRegexp(searchQuery), 'i');
		const preparedOptions = options.filter(({ title }) =>
			searchRegexp.test(title)
		);
		setOptionsList(preparedOptions);
	};

	useEffect(() => {
		if (fetchData && !_(apiParams).isEmpty()) {
			fetchDataDebounced.cancel();
			fetchData({ query: '' });
		} else {
			filterOptions(searchValue);
		}
	}, [options, fetchData]);

	useEffect(() => {
		if (searchable && searchValue && fetchDataDebounced) {
			fetchDataDebounced({ query: searchValue });
		} else if (!searchValue && previousSearchValue && fetchDataDebounced) {
			fetchDataDebounced({ query: '' });
		} else {
			filterOptions(searchValue);
		}
	}, [searchValue]);

	const onSelect = (value) => {
		setIsPopupVisible(false);
		setSelected(value);
	};

	const toggleList = () => {
		setSearchValue('');
		setIsPopupVisible((prevVisibility) => !prevVisibility);
	};

	const onAfterClose = () => {
		if (selected || (resetOption && selected === resetOption[valueField])) {
			onChange(name, selected);
			setSelected(null);
		}

		setSearchValue('');
	};

	const loadMore = useCallback(async () => {
		if (apiParams) {
			switch (apiParams?.paginationType) {
				case 'pagination':
					if (pagination?.current === pagination?.total) {
						return;
					}
					return fetchData({
						query: searchValue,
						extraParams: {
							...apiParams?.affectQueryParams,
							page: pagination?.current + 1
						},
						isLoadMore: true
					});
				default:
					return fetchData({
						query: searchValue,
						extraParams: {
							...apiParams?.affectQueryParams,
							limit: DEFAULT_LIMIT,
							offset: optionsList.length
						},
						isLoadMore: true
					});
			}
		}
	}, [optionsList, apiParams]);

	const handleClear = () => {
		const value = resetOption.sysName ?? resetOption.name;

		setSelected(value);
		onChange(name, value);
	};

	const resultSelectedOption = !_.isEmpty(selectedOption)
		? selectedOption
		: _(options).findWhere({ [valueField]: selectedValue }) ||
		  _(optionsList).findWhere({ [valueField]: selectedValue }) ||
		  resetOption;

	const initialTitleField = resultSelectedOption[controlTitleField];
	const isTitleLongerThanMaxValue =
		maxTitleLength && initialTitleField.length > maxTitleLength;

	const checkOnMaxValue = (positive, negative) =>
		isTitleLongerThanMaxValue ? positive : negative;

	const title = checkOnMaxValue(
		`${initialTitleField.slice(0, maxTitleLength)}...`,
		initialTitleField
	);

	const tooltip = checkOnMaxValue(
		`${label} ${resultSelectedOption[controlTitleField]}`.trim(),
		''
	);

	const getIsClearButtonShown = () => {
		if (!hasClearButton) {
			return false;
		}
		return resetOption.sysName
			? resetOption.sysName !== selectedValue
			: !!selectedValue;
	};

	const showClearButton = getIsClearButtonShown();

	return (
		<div className={styles.Select}>
			<div className={styles.Select__Controls} ref={controlRef}>
				<FilterButton
					className={cx({
						[styles.Select__ButtonWithAccent]: accent || isPopupVisible
					})}
					onClick={toggleList}
					reverse
					title={
						<>
							{label && title === 'России' && (
								<span className={styles.Select__Label}>{label}</span>
							)}
							<span>{title}</span>
						</>
					}
					titleClassName={titleClassName}
					tooltip={tooltip}
					marker={isPopupVisible ? 'up' : 'down'}
					theme={buttonTheme || 'bright'}
					icon={showClearButton ? undefined : 'arrow-down'}
					iconClassName={cx({
						[styles.Select__MarkerIconUp]: isPopupVisible,
						[styles.Select__MarkerIcon]: !isPopupVisible,
						[styles.Select__HiddenIcon]: showClearButton
					})}
					style={style}
				/>
				{showClearButton && (
					<FilterButton
						onClick={handleClear}
						icon="close-bold"
						className={styles.Select__ClearButton}
						iconClassName={styles.Select__ClearButtonIcon}
						tooltip="Очистить фильтр"
					/>
				)}
			</div>

			<Popup
				isOpen={isPopupVisible}
				element={controlRef.current}
				onClickOutside={toggleList}
				onExited={onAfterClose}
				placement="bottom-start"
				theme="widget"
				options={{
					strategy: 'absolute',
					modifiers: [
						{
							name: 'flip',
							options: {
								fallbackPlacements: ['bottom']
							}
						}
					]
				}}
			>
				<List
					isLoading={isLoading || isUpdating}
					searchable={searchable}
					onSearch={handleSearch}
					placeholder={placeholder}
					selectedValue={selectedValue}
					resetOption={resetOption}
					options={optionsList}
					onChange={onSelect}
					searchValue={searchValue}
					valueField={valueField}
					loadMore={loadMore}
					isLoadingMore={isLoadingMore}
				/>
			</Popup>
		</div>
	);
};

CustomSelect.propTypes = {
	name: PropTypes.string.isRequired,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string.isRequired,
			name: PropTypes.string
		})
	),
	apiParams: PropTypes.shape({
		url: PropTypes.string,
		dataPath: PropTypes.string,
		debounce: PropTypes.number,
		affectQueryParams: PropTypes.shape({}),
		additionalTitlePath: PropTypes.string,
		paginationType: PropTypes.oneOf(['pagination', 'offset']),
		withoutCache: PropTypes.bool
	}),
	isLoading: PropTypes.bool,
	selectedValue: PropTypes.node,
	selectedOption: PropTypes.shape({
		title: PropTypes.string,
		name: PropTypes.string
	}),
	onChange: PropTypes.func,
	searchable: PropTypes.bool,
	placeholder: PropTypes.string,
	resetOption: PropTypes.shape({
		title: PropTypes.string.isRequired,
		name: PropTypes.string,
		sysName: PropTypes.string
	}),
	valueField: PropTypes.string,
	controlTitleField: PropTypes.string,
	label: PropTypes.string,
	accent: PropTypes.bool,
	style: PropTypes.object,
	maxTitleLength: PropTypes.number,
	hasClearButton: PropTypes.bool,
	titleClassName: PropTypes.string,
	buttonTheme: PropTypes.oneOf(['simpleShape', 'classicField'])
};

CustomSelect.defaultProps = {
	options: []
};

export default CustomSelect;
